
import { defineComponent, ref } from "vue";
import { useI18n } from 'vue-i18n'
import { mapGetters, mapState } from "vuex";
import { EventBus } from "@/eventBus";
import { RootGetters } from "@/store/getters";
import { Likelihood } from "@/models/labels";
import { Insight, InsightType } from "@/models/insight";
import { BFF_BASE_PATH } from "@/core/utils"

export default defineComponent({
  setup() {
    const { t } = useI18n({
      inheritLocale: true,
      useScope: "local",
    });
    return { t };
  },
  data() {
    return {
      scale: 10,
      width: 510,
      height: 510,
      filterBy: '',
      isShow: false,
      insight: null as null | Insight,
    }
  },
  computed: {
    ...mapState(['currentSlide']),
    ...mapGetters({
      config: RootGetters.CONFIG,
      activeSlide: RootGetters.ACTIVE_SLIDE,
      pois: RootGetters.SLIDE_POIS,
    }),
    filteredPois() {
      /* HACK (Matan Narkiss)
      * Due to this task: https://ibex-ai-squad.monday.com/boards/3760637416/pulses/4221565161?userId=25900872
      * POIs were polluted by Algo team with the Bullseye coordinates, and the new logic is:
      * Show POIs only of insights of type "THRESHOLD" (low, medium, high). Other types of insights should NOT
      * support POI galleries
      */
      if (this.insight?.type !== InsightType.THRESHOLDED) return [];
      return this.pois.filter(poi => poi["POI Type"] === `screening_${this.filterBy}`);
    },
    insightTitle() {
      return this.t(`config.insight.slide.${this.currentSlide.tissueType}.${this.currentSlide.stain}.${this.insight?.key}.title`);
    },
  },
  methods: {
    open() {
      this.isShow = true
    },
    close() {
      this.isShow = false
    },
    calcImageURL(poi: any) {
      return `${BFF_BASE_PATH}/read_region_by_scale/${this.activeSlide.slideId
        .replace("/", "[@slash]")
        .replace("#", "[@pound]")}/${this.scale}/${poi["X Coordinate"] - (this.width / 2) * this.activeSlide.magnification / 10
        }/${poi["Y Coordinate"] - (this.height / 2) * this.activeSlide.magnification / this.scale}/${this.width}/${this.height}`;
    },
    showPOI(poi: any) {
      this.close();
      EventBus.emit("event-bridge-api", {
        eventName: "set-zoom-and-location-from-vue",
        data: { zoom: this.scale, x: poi["X Coordinate"], y: poi["Y Coordinate"] },
      });
    },
    galleryKeyListener(e: { key: string; }) {
      switch(e.key) {
        case 'g':
        case 'G':
          this.isShow = !this.isShow;
      }
    },
    activeInsightChanged ({ insight, insightObservation, isReportComplete }) {
      this.filterBy = insight?.key;
      this.insight = insight;
      // show by default if:
      // # configured to show by default
      // # there is an antive insight
      // # the insight is a low likelihood algo result
      // # the insight has no value (we only want to show the gallery in order to let the user add an observation to an empty insight)
      // # the report is not locked
      this.isShow = this.config.enable_poi_gallery_for_active_insight
        && !!this.filterBy
        && insight?.likelihood === Likelihood.LOW.toLowerCase()
        && !insightObservation.value
        && !isReportComplete;
    }
  },
  mounted() {
    EventBus.on('active-insight-changed', this.activeInsightChanged);
    window.addEventListener('keydown', this.galleryKeyListener , false);
  },
  unmounted() {
    EventBus.off('active-insight-changed', this.activeInsightChanged);
    window.removeEventListener('keydown', this.galleryKeyListener, false);
  }
});
